// Puerto de packages/hce/src/hce/paciente/PatientChartPage.tsx — mismo shell
// (banda de identidad + sidebar vertical de pestañas + Outlet), rehecho en
// Tailwind/shadcn. "Iniciar nueva consulta" ya NO cruza a la app vieja
// (packages/hce) — es un navigate() normal de React Router, a la pestaña
// Notas (ver iniciarConsulta más abajo). El catálogo completo de
// formularios (NuevaConsultaPage.tsx) se eliminó 2026-09-02 — cada grupo
// del Tramo B ya tiene su propia pestaña (GRUPOS_CON_PESTANA_PROPIA acá
// abajo) o vive en Laboratorio y Estudios/Documentos, así que la página
// aparte había quedado 100% redundante.
import { useMedplum, useResource, useSearchResources } from '@medplum/react-hooks';
import { formatHumanName } from '@medplum/core';
import type { AllergyIntolerance, Patient } from '@medplum/fhirtypes';
import { House } from 'lucide-react';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate, useParams } from 'react-router';
import { LinkTabs } from '@/components/LinkTabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { notify } from '@/components/ui/sonner';
import { TRAMO_B } from '@/lib/tramoB';
import { codigosDePaquetesDeEspecialidad, codigosFormularioDeEspecialidad } from '@/lib/catalogoEspecialidades';
import { useEspecialidadesHabilitadas } from '@/lib/useEspecialidadesHabilitadas';
import { useEspecialidadDelPracticante } from '@/lib/useEspecialidadDelPracticante';
import { useFarmaciaInternaHabilitada } from '@/lib/useFarmaciaInternaHabilitada';
import { esDirectorTecnicoFarmacia, useRolDelPracticante } from '@/lib/useRolDelPracticante';
import { estadoArchivoDe } from '@/lib/archivoEstado';
import { cerrarSesionHce } from '@/auth/cerrarSesion';
import { busquedaDeEncounter } from '@/lib/enlaceEncounter';
import { BotonSalaEnEspera } from '@/dashboard/BotonSalaEnEspera';
import { accesoDenegadoDe, type CodigoAccesoDenegado } from './accesoDenegado';
import { declararEmergencia, puedeEmergenciaDe } from './accesoEmergencia';
import { AvisoAccesoDenegado } from './AvisoAccesoDenegado';
import { reportarHallazgos } from './alertaCompletitudStore';
import { ConfirmarTomaDialog, type TomaPendiente } from './ConfirmarTomaDialog';
import { decidirInicioEnSala } from './salaAlIniciar';
import { asegurarEncounterDeHoy, buscarEncounterAbiertoDePaciente, esperarEncounterAmbulatorioAbierto } from './asegurarEncounterDeHoy';
import { evaluarCompletitudAlSalir } from './chequeoCompletitudConsulta';
import { calcularGruposHabilitados, extrasSueltos } from './gruposHabilitados';
import { reclasificarArchivoDePaciente } from './vaultClient';
import { enlacesDeFusion } from './fusionEnlaces';
import { tratamientoLimitado } from './limitacionTratamiento';
import { IdentidadMinimizable } from './IdentidadMinimizable';

/** Ver `useOutletContext` en NotasTab.tsx. */
export type PatientChartOutletContext = {
  patient: Patient;
  patientId: string;
  extrasSueltos: string[];
  /**
   * Pedido explícito de Sue (2026-09-27): ningún formulario puede quedar
   * "pendiente de firma" abandonado — o se firma, o se descarta. Cada
   * orquestador (FormularioDiscretoPage/Formulario001Page/
   * MotorFormulario/MotorFormularioConsentimiento/FormularioFemoPage/
   * RevocatoriaConsentimiento024) llama esto con `true` apenas guarda algo
   * sin firmar todavía, y con `false` al firmar/descartar/desmontarse. Ver
   * `confirmarSalida` más abajo, que lo lee para bloquear "Inicio"/"Cerrar
   * sesión"/las pestañas del sidebar (LinkTabs) mientras esté en `true`.
   */
  reportarPendienteFirma: (pendiente: boolean) => void;
};

// diagnostico → pestaña "Laboratorio y Estudios" existente, documentos →
// pestaña "Documentos" existente — el resto de los grupos del Tramo B suma
// una pestaña cada uno, gateada por especialidad-habilitada.
const GRUPOS_CON_PESTANA_PROPIA = Array.from(new Set(TRAMO_B.map((e) => e.grupo))).filter((grupo) => grupo !== 'diagnostico' && grupo !== 'documentos');

function edadDe(birthDate: string | undefined): number | undefined {
  if (!birthDate) return undefined;
  const nacimiento = new Date(birthDate);
  const ahora = new Date();
  let edad = ahora.getFullYear() - nacimiento.getFullYear();
  const aunNoCumple = ahora.getMonth() < nacimiento.getMonth() || (ahora.getMonth() === nacimiento.getMonth() && ahora.getDate() < nacimiento.getDate());
  if (aunNoCumple) edad--;
  return edad;
}

function inicialesDe(patient: Patient): string {
  const given = patient.name?.[0]?.given?.[0]?.[0] ?? '';
  const family = patient.name?.[0]?.family?.[0] ?? '';
  return (given + family).toUpperCase() || '·';
}

/**
 * "Reclasificar ahora" (Modo Auditor sección B, 2026-09-10) — el barrido
 * nocturno (retention-sweep.ts) es el único lugar que hoy calcula
 * activo/pasivo; el checklist de auditoría solo lo LEE. Este badge, junto a
 * la identidad del paciente (mismo lugar que AlergiaChip), es la primera
 * pantalla real donde el consultorio ve y puede forzar ese estado sin
 * esperar al cron ni mirar la base de datos.
 */
function ArchivoBadge({ patient, patientId }: { patient: Patient; patientId: string }): JSX.Element {
  const { t } = useTranslation();
  const [estadoForzado, setEstadoForzado] = useState<'activo' | 'pasivo' | null>(null);
  const [reclasificando, setReclasificando] = useState(false);
  const estado = estadoForzado ?? estadoArchivoDe(patient);

  async function reclasificar(): Promise<void> {
    setReclasificando(true);
    try {
      const resultado = await reclasificarArchivoDePaciente(patientId);
      if (resultado.estado) setEstadoForzado(resultado.estado);
      else notify({ color: 'yellow', message: resultado.mensaje ?? t('fichaPaciente.sinDatos') });
    } catch (err) {
      notify({ color: 'red', message: err instanceof Error ? err.message : String(err), autoClose: false });
    }
    setReclasificando(false);
  }

  return (
    <div className="flex items-center gap-2">
      <Badge variant={estado === 'activo' ? 'sage' : estado === 'pasivo' ? 'outline' : 'clay'} className="font-mono text-[10px] uppercase">
        {estado === 'activo' ? t('fichaPaciente.archivo.activo') : estado === 'pasivo' ? t('fichaPaciente.archivo.pasivo') : t('fichaPaciente.archivo.sinClasificar')}
      </Badge>
      <button
        type="button"
        className="text-xs text-hce-pine underline-offset-2 hover:underline disabled:opacity-50"
        disabled={reclasificando}
        onClick={() => void reclasificar()}
      >
        {reclasificando ? t('fichaPaciente.archivo.reclasificando') : t('fichaPaciente.archivo.reclasificarAhora')}
      </button>
    </div>
  );
}

function AlergiaChip({ patientId }: { patientId: string }): JSX.Element {
  const { t } = useTranslation();
  const [alergias, cargando] = useSearchResources('AllergyIntolerance', { patient: `Patient/${patientId}` });

  if (cargando) return <></>;

  const lista = (alergias ?? []) as AllergyIntolerance[];
  if (lista.length === 0) {
    return <span className="text-xs text-muted-foreground">{t('fichaPaciente.sinAlergias')}</span>;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {lista.map((a) => (
        <Badge key={a.id} variant="clay" className="font-mono text-[10px] uppercase">
          {t('fichaPaciente.alergias')} · {a.code?.text ?? '—'}
        </Badge>
      ))}
    </div>
  );
}

export function PatientChartPage(): JSX.Element {
  const { t } = useTranslation();
  const { id } = useParams() as { id: string };
  const navigate = useNavigate();
  const medplum = useMedplum();
  const patient = useResource<Patient>({ reference: `Patient/${id}` });
  const especialidades = useEspecialidadesHabilitadas();
  const especialidadPracticante = useEspecialidadDelPracticante();
  const farmaciaInterna = useFarmaciaInternaHabilitada();
  const rolEstado = useRolDelPracticante();
  const esFarmacia = esDirectorTecnicoFarmacia(rolEstado);
  const [iniciando, setIniciando] = useState(false);
  // MT-042: el servidor niega abrir el expediente (sin vínculo asistencial vigente o sin consentimiento vigente): se avisa en vez de quedar cargando. La lectura usa
  // la caché del cliente de Medplum, así que no pide el Patient dos veces.
  const [denegado, setDenegado] = useState<CodigoAccesoDenegado | null>(null);
  const [puedeEmergencia, setPuedeEmergencia] = useState(false);
  useEffect(() => {
    let vigente = true;
    setDenegado(null);
    medplum.readResource('Patient', id).catch((err: unknown) => {
      const codigo = accesoDenegadoDe(err);
      if (vigente && codigo) {
        setDenegado(codigo);
        setPuedeEmergencia(puedeEmergenciaDe(err));
      }
    });
    return () => {
      vigente = false;
    };
  }, [medplum, id]);

  // Bloqueo de salida sin firmar/descartar (pedido explícito de Sue,
  // 2026-09-27) — ver el comentario largo en `PatientChartOutletContext`
  // arriba. `beforeunload` cubre cerrar la pestaña/recargar (el navegador
  // solo muestra SU propio texto genérico, no el de `mensajeConfirmarSalida`
  // — no hay forma de personalizarlo, es una limitación de todos los
  // navegadores modernos por seguridad). `confirmarSalida` cubre la
  // navegación DENTRO de la SPA (Inicio/Cerrar sesión/LinkTabs) — la app usa
  // `<BrowserRouter>` clásico, no un data router, así que no existe
  // `useBlocker` acá; en su lugar cada punto de salida llama esto a mano
  // antes de navegar.
  const [pendienteFirma, setPendienteFirma] = useState(false);
  useEffect(() => {
    if (!pendienteFirma) return;
    const avisar = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', avisar);
    return () => window.removeEventListener('beforeunload', avisar);
  }, [pendienteFirma]);
  function confirmarSalida(): boolean {
    return !pendienteFirma || window.confirm(t('formulario.confirmarSalidaSinFirmar'));
  }

  // Alertas de completitud EN VIVO (2026-09-10) — el chequeo corre recién al
  // DESMONTAR este componente (cleanup), que es cuando de verdad "se sale de
  // la ficha": cambiar de paciente (id nuevo) o navegar fuera del todo.
  // Cambiar de PESTAÑA interna (historia/notas/etc.) no desmonta
  // PatientChartPage — vive bajo el mismo <Outlet>, así que no dispara nada
  // acá (correcto: seguís en la misma consulta). Reporta al store del popup
  // (AlertaCompletitudPopup.tsx) en vez de mostrar nada acá mismo — para
  // cuando esto corre ya no hay ficha visible a la que anclar un diálogo.
  useEffect(() => {
    return () => {
      void evaluarCompletitudAlSalir(medplum, id).then((hallazgos) => {
        if (hallazgos.length > 0) reportarHallazgos(id, hallazgos);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- medplum es estable (mismo cliente de todo el árbol); solo debe reprogramarse el cleanup cuando cambia el paciente (id)
  }, [id]);

  // Toma automática (Sala CRM↔HCE, rebanada 5, C11.5): el turno de hoy de este paciente es de OTRO médico; se pide confirmación antes de atenderlo.
  const [toma, setToma] = useState<TomaPendiente | null>(null);

  const iniciarConsulta = async (confirmarToma = false): Promise<void> => {
    setIniciando(true);
    try {
      // La Sala decide primero (turno propio / toma / otra sede). Nunca bloquea la atención por un fallo suyo (ver `decidirInicioEnSala`).
      const decision = await decidirInicioEnSala(id, confirmarToma);
      if (decision.tipo === 'detener') {
        notify({ color: 'red', message: t('sala.toma.otraSede'), autoClose: 8000 });
        setIniciando(false);
        return;
      }
      if (decision.tipo === 'confirmarToma') {
        setToma({ deMedico: decision.deMedico, especialidadDistinta: decision.especialidadDistinta });
        setIniciando(false);
        return;
      }
      setToma(null);
      if (decision.aviso?.clave === 'consultaDeOtro') notify({ color: 'yellow', message: t('sala.toma.consultaDeOtro', { medico: decision.aviso.medico ?? '—' }), autoClose: 8000 });
      else if (decision.aviso?.clave === 'salaLenta') {
        notify({ color: 'yellow', message: t('sala.resultado.salaLenta'), autoClose: 9000 });
        // El servidor puede seguir abriendo el Encounter: se espera y se vuelve a buscar antes de crear uno propio (no duplicar Encounters AMB).
        await esperarEncounterAmbulatorioAbierto(medplum, id);
      }
      // Asegura el Encounter de hoy (mismo criterio de siempre — sostiene el
      // indicador "en consulta" del Dashboard) pero ya no navega al catálogo
      // completo de Formularios: pedido explícito del usuario 2026-09-02,
      // aterriza en Notas (que ya tenía su propio picker acotado a Tramo A +
      // el historial de notas). El catálogo completo sigue accesible desde
      // un link dentro de Notas.
      await asegurarEncounterDeHoy(medplum, id);
      // Absoluto (desde la raíz del router, después del basename) — mismo
      // criterio que LinkTabs.tsx, para no depender de la resolución
      // relativa de React Router según qué pestaña esté activa.
      navigate(`/${id}/notas`);
    } catch (err) {
      notify({ color: 'red', message: err instanceof Error ? err.message : String(err), autoClose: false });
    }
    setIniciando(false);
  };

  // Rediseño mayor 2026-08-31 (Módulo F) — el filtro REAL de qué pestaña de
  // especialidad ve el médico pasa de ser tenant-wide a ser por practicante
  // (codigosFormularioDeEspecialidad, catalogoEspecialidades.ts), sin dejar
  // de respetar el techo tenant-wide ya existente — ver
  // calcularGruposHabilitados (gruposHabilitados.ts, pura y testeada aparte).
  const cargandoGrupos = especialidades.cargando || especialidadPracticante.cargando;
  const tieneEspecialidad = !especialidadPracticante.cargando && especialidadPracticante.codigo !== null;
  const codigosDelPracticante = tieneEspecialidad ? codigosFormularioDeEspecialidad(especialidadPracticante.codigo!) : null;
  // Solo paquetes/base/universales (SIN extras) — un extra suelto (ej.
  // esc-phq9 en Medicina General) no debe destrabar la pestaña completa de
  // un grupo ajeno. Ver el comentario largo en catalogoEspecialidades.ts.
  const codigosDePaquetesDelPracticante = tieneEspecialidad ? codigosDePaquetesDeEspecialidad(especialidadPracticante.codigo!) : null;
  const gruposHabilitados = cargandoGrupos
    ? []
    : calcularGruposHabilitados(GRUPOS_CON_PESTANA_PROPIA, TRAMO_B, especialidades.codigos, codigosDePaquetesDelPracticante);
  const extrasSueltosDelPracticante = cargandoGrupos ? [] : extrasSueltos(TRAMO_B, codigosDePaquetesDelPracticante, codigosDelPracticante);

  // Orden de pestañas (2026-09-11, pedido explícito) — antes era el orden
  // arbitrario en que se fueron construyendo los módulos. Reordenado para
  // seguir la secuencia real de una consulta:
  //   1) Panorama al abrir la ficha (resumen).
  //   2) Chequeos que se revisan ANTES de examinar/prescribir: signos
  //      vitales (ya tomados casi siempre antes de que entre el médico),
  //      historia (antecedentes), alergias y medicamentos vigentes (mismo
  //      chequeo de seguridad, farmacia va pegada por ser la misma familia).
  //   3) El trabajo de HOY: problemas activos, la nota de la consulta,
  //      formularios propios de la especialidad, laboratorio.
  //   4) Cierre de la consulta: agendar seguimiento.
  //   5) Uso esporádico/administrativo, al final: documentos,
  //      consentimientos, datos demográficos (lo que casi nunca se edita).
  const tabsCompletas = [
    { label: t('fichaPaciente.tabs.facesheet'), value: 'resumen' },
    { label: t('fichaPaciente.tabs.signosVitales'), value: 'signos-vitales' },
    { label: t('fichaPaciente.tabs.historia'), value: 'historia' },
    { label: t('fichaPaciente.tabs.alergias'), value: 'alergias' },
    { label: t('fichaPaciente.tabs.medicamentos'), value: 'medicamentos' },
    // Pedido explícito del usuario 2026-09-11: antes se sumaba para CUALQUIER
    // especialidad con el flag de tenant activo (farmaciaInterna.habilitada),
    // así que cualquier médico veía la pestaña "Farmacia" en la ficha de
    // cualquier paciente. Ahora queda exclusiva del Director Técnico Q.F./B.F.
    // (esFarmacia) — el resto de especialidades ya no la ve. Va pegada a
    // Medicamentos por ser la misma familia (medicación).
    ...(esFarmacia && !farmaciaInterna.cargando && farmaciaInterna.habilitada ? [{ label: t('hub.farmaciaInterna'), value: 'farmacia' }] : []),
    { label: t('fichaPaciente.tabs.problemas'), value: 'problemas' },
    { label: t('fichaPaciente.tabs.notas'), value: 'notas' },
    ...gruposHabilitados.map((grupo) => ({ label: t(`hub.grupo.${grupo}`), value: grupo })),
    { label: t('fichaPaciente.tabs.labs'), value: 'laboratorio' },
    { label: t('fichaPaciente.tabs.cobro'), value: 'cobro' },
    { label: t('fichaPaciente.tabs.seguimiento'), value: 'seguimiento' },
    { label: t('fichaPaciente.tabs.documentos'), value: 'documentos' },
    { label: t('fichaPaciente.tabs.consentimientos'), value: 'consentimientos' },
    { label: t('fichaPaciente.tabs.datosDemograficos'), value: 'datos-demograficos' },
    // H-003: quién modificó cada registro y cuándo (versiones de Medplum), de solo lectura. Uso esporádico: al final.
    { label: t('fichaPaciente.tabs.historialCambios'), value: 'historial-cambios' },
  ];
  // Enfermería (Fase 3, "MSP + nota SOAPIE"): Signos Vitales (lee el 020 y
  // su balance hídrico) + Kardex (022) + Intervenciones (120) + Nota de turno
  // (SOAPIE, código propio no MSP). El resto de la ficha se oculta del sidebar
  // (mismo criterio que el Director Técnico Q.F./B.F. de abajo: las rutas
  // siguen siendo válidas si se llega por link directo).
  const esEnfermeria = !especialidadPracticante.cargando && especialidadPracticante.codigo === 'enfermeria';
  const tabsEnfermeria = [
    { label: t('fichaPaciente.tabs.signosVitales'), value: 'signos-vitales' },
    { label: t('fichaPaciente.tabs.kardex'), value: 'kardex' },
    { label: t('fichaPaciente.tabs.intervenciones'), value: 'intervenciones' },
    { label: t('fichaPaciente.tabs.notaTurno'), value: 'nota-turno' },
    // Decisión explícita del usuario 2026-09-14: Enfermería SÍ ve "Cobro" —
    // puede armar/enviar la atención sin necesitar a un médico para eso.
    { label: t('fichaPaciente.tabs.cobro'), value: 'cobro' },
    // Auditoría de visibilidad por especialidad (2026-09-27): 059, 116-cert,
    // 122, 053, 119, Anexo2 y 008 son extras de Enfermería (catalogoEspecialidades.ts)
    // que hasta ahora no tenían NINGÚN enlace en su sidebar reducido — quedaban
    // con permiso para guardarlos pero sin ningún botón que llevara ahí. Las
    // 4 pestañas de abajo son las mismas que ya usa el resto de especialidades
    // (Laboratorio y Estudios/Documentos/Referencias/Seguridad del Paciente),
    // ninguna crea un Encounter (a diferencia de "Notas"), así que no chocan
    // con el AccessPolicy "Enfermera" (Encounter en readonly). "Emergencia"
    // (008) es la única sin pestaña compartida — ver la ruta dedicada nueva
    // en routes.tsx, mismo patrón que kardex/intervenciones.
    { label: t('fichaPaciente.tabs.labs'), value: 'laboratorio' },
    { label: t('fichaPaciente.tabs.documentos'), value: 'documentos' },
    { label: t('hub.grupo.referencias'), value: 'referencias' },
    { label: t('hub.grupo.seguridad-paciente'), value: 'seguridad-paciente' },
    { label: t('hub.formulario.008'), value: 'emergencia' },
  ];

  // Director Técnico Q.F./B.F. (2026-09-11): pedido explícito — solo ve
  // Farmacia y Medicamentos, el resto de la ficha del paciente queda
  // invisible (cinturón y tirantes: el nav general ya lo manda a /farmacia,
  // esto cubre si de todos modos llega acá por un link directo).
  const tabs = esFarmacia
    ? tabsCompletas.filter((tab) => tab.value === 'farmacia' || tab.value === 'medicamentos')
    : esEnfermeria
      ? tabsEnfermeria
      : tabsCompletas;

  // Enfermería (2026-09-13, pendiente confirmado por el revisor funcional):
  // los formularios de las pestañas de Enfermería (Kardex 022, Intervenciones
  // 120, Nota de turno) son rutas hijas de un `LinkTabs`, que armaba
  // `to={baseUrl}/{value}` SIN query — y FormularioDiscretoPage lee el
  // Encounter de `?encounter=`, así que todo lo que registraba una enfermera
  // quedaba sin vínculo a la consulta. Acá se resuelve el Encounter ABIERTO
  // del paciente (estancia IMP o consulta AMB en curso) y se lo cuelga a cada
  // pestaña. SOLO LECTURA a propósito: una enfermera no abre consultas —
  // `asegurarEncounterDeHoy` (que sí crea) queda para "Iniciar nueva
  // consulta", botón que Enfermería ya no ve, y el AccessPolicy "Enfermera"
  // tiene `Encounter` en `readonly`. Sin Encounter abierto el registro queda
  // sin vínculo, igual que hasta hoy.
  const [encounterAbierto, setEncounterAbierto] = useState<string>();
  useEffect(() => {
    if (!esEnfermeria) {
      setEncounterAbierto(undefined);
      return;
    }
    let cancelado = false;
    buscarEncounterAbiertoDePaciente(medplum, id)
      .then((e) => {
        if (!cancelado) setEncounterAbierto(e?.id ? `Encounter/${e.id}` : undefined);
      })
      .catch(() => {
        // Sin Encounter resoluble se sigue como antes (registro sin vínculo);
        // no vale la pena molestar a la enfermera con un toast por esto.
        if (!cancelado) setEncounterAbierto(undefined);
      });
    return () => {
      cancelado = true;
    };
  }, [medplum, id, esEnfermeria]);

  if (denegado) return (
      <AvisoAccesoDenegado
        codigo={denegado}
        onVolver={() => navigate('/')}
        puedeEmergencia={puedeEmergencia}
        onDeclarar={(motivo) => declararEmergencia(id, motivo)}
        onAbierto={() => window.location.reload()}
      />
    );

  if (!patient) {
    return (
      <div className="mx-auto max-w-6xl py-8">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-hce-pine border-t-transparent" />
      </div>
    );
  }

  const nombre = patient.name?.[0] ? formatHumanName(patient.name[0]) : id;
  const edad = edadDe(patient.birthDate);
  const generoInicial = patient.gender ? t(`fichaPaciente.genero.${patient.gender}`, patient.gender).charAt(0) : undefined;
  const identificacion = patient.identifier?.[0]?.value;
  // Fusión de fichas duplicadas (C-004): el registro que se unió a otro (`replaced-by`) avisa y no inicia consultas nuevas; el vigente (`replaces`) avisa
  // que existe un registro unido. La historia no se mueve: solo se enlaza.
  const enlacesFusion = enlacesDeFusion(patient);
  const reemplazadoPor = enlacesFusion.find((e) => e.tipo === 'replaced-by');
  const reemplaza = enlacesFusion.filter((e) => e.tipo === 'replaces');

  return (
    <div className="mx-auto max-w-6xl py-4">
      <div className="rounded-lg border border-border bg-card">
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-border p-4">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => confirmarSalida() && navigate(esFarmacia ? '/farmacia' : '/dashboard')} className="shrink-0">
              <House className="size-4" />
              {t('fichaPaciente.inicio')}
            </Button>
            {/* Contador de pacientes en espera, visible también dentro de la ficha (la ficha no usa el menú superior). */}
            {!esFarmacia && <BotonSalaEnEspera onClick={() => confirmarSalida() && navigate('/sala')} />}
            <Button variant="outline" size="sm" onClick={() => confirmarSalida() && void cerrarSesionHce(medplum, navigate)} className="shrink-0">
              {t('fichaPaciente.cerrarSesion')}
            </Button>
            <div className="grid size-[34px] shrink-0 place-items-center rounded-md bg-secondary">
              <span className="font-mono text-xs font-semibold">{inicialesDe(patient)}</span>
            </div>
            <div className="flex flex-col gap-1">
              <IdentidadMinimizable
                pacienteId={id}
                nombre={nombre}
                iniciales={inicialesDe(patient)}
                edadGenero={edad !== undefined && generoInicial ? `${edad} ${generoInicial}` : undefined}
                nacimiento={patient.birthDate}
                identificacion={identificacion}
              >
                <ArchivoBadge patient={patient} patientId={id} />
                {tratamientoLimitado(patient) && (
                  <Badge variant="destructive" className="font-mono text-[10px] uppercase" title={t('fichaPaciente.limitacion.detalle')}>
                    {t('fichaPaciente.limitacion.etiqueta')}
                  </Badge>
                )}
              </IdentidadMinimizable>
              <AlergiaChip patientId={id} />
            </div>
          </div>
          {/* Director Técnico Q.F./B.F. (2026-09-11) no atiende consultas — solo
              dispensa. Enfermería (2026-09-13) tampoco abre consultas: el
              AccessPolicy "Enfermera" tiene `Encounter` en readonly, así que el
              botón solo podía terminar en un 403. */}
          {!esFarmacia && !esEnfermeria && (
            <Button
              onClick={() => void iniciarConsulta()}
              disabled={iniciando || Boolean(reemplazadoPor)}
              title={reemplazadoPor ? t('fichaPaciente.fusion.consultaBloqueada') : undefined}
              className="bg-hce-ink text-hce-paper hover:bg-hce-ink/90"
            >
              {t('fichaPaciente.iniciarConsulta')}
            </Button>
          )}
          <ConfirmarTomaDialog toma={toma} ocupado={iniciando} onConfirmar={() => void iniciarConsulta(true)} onCancelar={() => setToma(null)} />
        </div>
        {(reemplazadoPor || reemplaza.length > 0) && (
          <div role="note" className="space-y-1 border-b border-border bg-hce-clay/10 px-4 py-2 text-sm">
            {reemplazadoPor && (
              <p>
                {t('fichaPaciente.fusion.reemplazadoPor')}{' '}
                <button type="button" className="font-medium underline" onClick={() => confirmarSalida() && navigate(`/${reemplazadoPor.id}`)}>
                  {t('fichaPaciente.fusion.abrirVigente')}
                </button>
              </p>
            )}
            {reemplaza.map((e) => (
              <p key={e.id}>
                {t('fichaPaciente.fusion.reemplaza')}{' '}
                <button type="button" className="font-medium underline" onClick={() => confirmarSalida() && navigate(`/${e.id}`)}>
                  {t('fichaPaciente.fusion.abrirUnido')}
                </button>
              </p>
            ))}
          </div>
        )}
        {/* `minmax(0,1fr)`, no `1fr` a secas: un track `1fr` mide su mínimo
            como `auto` (el ancho intrínseco del contenido) salvo que se le
            fije un piso — una tabla ancha sin recorte (Kardex 022,
            Intervenciones 120: grilla de 24 horas) empujaba esta columna, la
            tarjeta y la página entera fuera de sus márgenes en vez de
            scrollear dentro de su propio `overflow-x-auto`. Bug de layout
            compartido por toda la ficha, reportado en vivo 2026-09-14. */}
        <div className="grid grid-cols-1 sm:grid-cols-[12rem_minmax(0,1fr)]">
          <div className="border-b border-border p-2 sm:border-r sm:border-b-0">
            <LinkTabs baseUrl={`/${id}`} tabs={tabs} orientation="vertical" search={busquedaDeEncounter(encounterAbierto)} confirmarSalida={confirmarSalida} />
          </div>
          <div className="p-4">
            <Outlet
              context={
                { patient, patientId: id, extrasSueltos: extrasSueltosDelPracticante, reportarPendienteFirma: setPendienteFirma } satisfies PatientChartOutletContext
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
}
